import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../utils/axiosConfig';
import UploadAvatar from '../pages/UploadAvatar';
import { useDispatch } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import { getClientTypeMeta } from '../../utils/clientTypes';
import {
    Settings as SettingsIcon,
    Store,
    MessageCircle,
    Mail,
    CreditCard,
    Save,
    ShieldCheck,
    AlertCircle,
    ArrowUp,
    Info,
    MapPin,
    CheckCircle2,
    Loader2,
    X
} from 'lucide-react';

const Settings = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { isAuthenticated, user } = useSelector((state) => state.auth);

    const [cliente, setCliente] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [toast, setToast] = useState(null);
    const [saveStatus, setSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'
    const [showMpGuide, setShowMpGuide] = useState(false);
    const [onboardingHint, setOnboardingHint] = useState(null);

    const [formData, setFormData] = useState({
        nombre: '',
        description: '',
        adminEmail: '',
        contactPhone: '',
        taxId: '',
        address: '',
        province: '',
        city: ''
    });

    const isInitialMount = useRef(true);
    const timerRef = useRef(null);
    const brandSectionRef = useRef(null);
    const contactSectionRef = useRef(null);
    const mpSectionRef = useRef(null);

    useEffect(() => {
        fetchCliente();
    }, [slug]);

    const fetchCliente = async () => {
        try {
            const res = await axios.get(`/api/clients/${slug}`);
            setCliente(res.data);
            setFormData({
                nombre: res.data.nombre || '',
                description: res.data.description || '',
                adminEmail: res.data.adminEmail || '',
                contactPhone: res.data.contactPhone || '',
                taxId: res.data.taxId || '',
                address: res.data.address || '',
                province: res.data.province || '',
                city: res.data.city || ''
            });
            // Marcamos que la carga inicial terminó para habilitar el autoguardado
            setTimeout(() => {
                isInitialMount.current = false;
            }, 1000);
        } catch (err) {
            console.error('Error al obtener cliente:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async (e) => {
        if (e) e.preventDefault();
        setUpdating(true);
        setSaveStatus('saving');
        try {
            await axios.put(`/api/clients/${cliente._id}`, {
                ...cliente,
                ...formData
            });
            setSaveStatus('saved');
            // No mostramos toast en autoguardado para no molestar,
            // pero si es manual (viene con evento 'e'), sí lo mostramos.
            if (e) showToast('Ajustes guardados correctamente.', 'success');
            
            // Volver a 'idle' después de un rato de mostrar 'saved'
            setTimeout(() => setSaveStatus('idle'), 3000);
        } catch (err) {
            console.error('Error al actualizar ajustes:', err);
            setSaveStatus('error');
            showToast('No se pudieron guardar los cambios.', 'error');
        } finally {
            setUpdating(false);
        }
    };

    // Lógica de Autoguardado (Debounce)
    useEffect(() => {
        if (isInitialMount.current) return;

        if (timerRef.current) clearTimeout(timerRef.current);
        
        setSaveStatus('idle'); // Reset status when typing starts

        timerRef.current = setTimeout(() => {
            handleUpdate();
        }, 1500);

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [formData, cliente?.commissionConfig?.payer]);

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 4000);
    };

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('mercadopago') === 'ok') {
            showToast('Mercado Pago conectado correctamente. Ya podés cobrar online.', 'success');
            fetchCliente();
            navigate(`/${slug}/admin/settings?focus=mp&connected=1`, { replace: true });
        }
    }, [slug, navigate]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const focus = params.get('focus');
        const justConnected = params.get('connected') === '1';

        if (loading || !focus) return;

        const focusConfig = {
            mp: {
                ref: mpSectionRef,
                title: 'Conectá Mercado Pago',
                body: 'Este paso habilita el cobro online. Tocá el botón de conexión y volvé a PASSAR cuando Mercado Pago termine.'
            },
            brand: {
                ref: brandSectionRef,
                title: 'Completá tu marca',
                body: 'Subí un logo y escribí una biografía corta. Eso ayuda a que tus compradores reconozcan tu organización.'
            },
            contact: {
                ref: contactSectionRef,
                title: 'Cargá tus datos de contacto',
                body: 'Agregá email y WhatsApp para que tus clientes sepan dónde consultar después de comprar.'
            }
        };

        const config = focusConfig[focus];
        if (!config) return;

        setOnboardingHint({
            key: focus,
            title: config.title,
            body: config.body
        });

        setTimeout(() => {
            config.ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 250);

        if (focus === 'mp' && !justConnected && !cliente?.hasMpConnected) {
            setTimeout(() => setShowMpGuide(true), 500);
        }
    }, [loading, cliente?.hasMpConnected]);

    const startMPConnect = async () => {
        try {
            const response = await axios.get(`/mercadopago/connect/${slug}`);
            window.location.href = response.data.url;
        } catch (error) {
            showToast('Error al conectar con Mercado Pago.', 'error');
        }
    };

    const handleMPConnect = () => {
        setShowMpGuide(true);
    };

    const handleMPDisconnect = async () => {
        try {
            await axios.put(`/api/clients/${cliente._id}`, {
                ...cliente,
                accessToken: '',
                refreshToken: '',
                mpUserId: '',
                mpConnectedAt: null,
            });
            showToast('Mercado Pago desconectado.', 'success');
            fetchCliente();
        } catch (err) {
            showToast('Error al desconectar.', 'error');
        }
    };

    const handleDeleteOrganization = async () => {
        const confirmDelete = window.confirm(
            "⚠️ ZONA DE PELIGRO ⚠️\n\n" +
            "¿Estás completamente seguro de que deseas eliminar esta organización?\n\n" +
            "• Se cancelará inmediatamente tu cuenta y suscripción de Mercado Pago asociada a esta app.\n" +
            "• Se eliminarán permanentemente todos los eventos, estadísticas y miembros asociados.\n" +
            "• El nombre de tu URL quedará liberado.\n\n" +
            "ESTA ACCIÓN NO SE PUEDE DESHACER."
        );

        if (!confirmDelete) return;

        // Double confirmation for safety
        const randomCode = Math.floor(1000 + Math.random() * 9000);
        const confirmCode = window.prompt(`Para confirmar la eliminación definitiva, ingresa el siguiente código de 4 dígitos: ${randomCode}`);
        
        if (confirmCode !== randomCode.toString()) {
            showToast("Código incorrecto, eliminación cancelada.", "error");
            return;
        }

        try {
            setUpdating(true);
            await axios.delete(`/api/clients/${cliente._id}`);
            alert("Organización eliminada correctamente. Esperamos tenerte de vuelta en el futuro.");
            
            // Refrescar estado global limpiando credenciales o forzando reload al dashboard
            navigate("/");
            window.location.reload(); // Forzar recarga limpia para resetear roles heredados
        } catch (err) {
            console.error(err);
            showToast("Ocurrió un error al intentar eliminar la organización. Contáctate con soporte.", "error");
            setUpdating(false);
        }
    };


    if (loading) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
                <div className="w-12 h-12 border-4 border-primary-orange/20 border-t-primary-orange rounded-full animate-spin"></div>
                <p className="text-white/20 font-bold uppercase tracking-widest text-xs">Cargando Ajustes...</p>
            </div>
        );
    }

    const isSportsClient = cliente?.organizationType === 'sports';
    const organizationMeta = getClientTypeMeta(cliente?.organizationType);
    const organizationLabel = organizationMeta.shortLabel;
    const serviceChargePercentage = isSportsClient ? 15 : 14;
    const serviceChargeExample = isSportsClient ? 150 : 140;
    const organizationPaysCommission = cliente?.commissionConfig?.payer === 'salon';
    const platformFeePercentage = isSportsClient ? 5 : 4;
    const platformFeeExample = 1000 * (platformFeePercentage / 100);
    const customerTotalExample = organizationPaysCommission ? 1000 : 1000 + serviceChargeExample;
    const organizationReceivesExample = organizationPaysCommission
        ? 1000 - platformFeeExample
        : 1000 + serviceChargeExample - platformFeeExample;

    return (
        <div className="max-w-5xl mx-auto py-10 px-4 pb-32 animate-fade-in relative">
            {/* Toast Notification */}
            {toast && (
                <div className={`fixed top-24 right-4 z-[100] px-6 py-3 rounded-2xl border shadow-2xl animate-fade-in-up flex items-center gap-3 backdrop-blur-xl
          ${toast.type === 'success' ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-red-500/10 border-red-500/20 text-red-400'}`}>
                    <div className={`w-2 h-2 rounded-full animate-pulse ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`} />
                    <span className="font-bold text-sm tracking-tight">{toast.message}</span>
                </div>
            )}

            {showMpGuide && (
                <div className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
                    <div className="w-full max-w-lg bg-surface border border-white/10 rounded-[32px] shadow-2xl overflow-hidden">
                        <div className="h-1 bg-brand-gradient"></div>
                        <div className="p-8 relative">
                            <button
                                type="button"
                                onClick={() => setShowMpGuide(false)}
                                className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/5 text-white/40 hover:text-white flex items-center justify-center"
                            >
                                <X size={18} />
                            </button>

                            <div className="w-16 h-16 rounded-2xl bg-primary-orange/10 text-primary-orange flex items-center justify-center mb-6">
                                <CreditCard size={32} />
                            </div>

                            <p className="text-[10px] font-black uppercase tracking-[3px] text-white/30 mb-3">Conexión segura</p>
                            <h2 className="text-3xl font-black text-white tracking-tight leading-tight mb-4">
                                Vas a conectar tu Mercado Pago
                            </h2>
                            <p className="text-white/45 text-sm font-medium leading-relaxed mb-6">
                                PASSAR te va a llevar a Mercado Pago. Iniciá sesión con la cuenta donde querés cobrar, aceptá los permisos y vas a volver automáticamente a la plataforma.
                            </p>

                            <div className="space-y-3 mb-8">
                                {[
                                    'No cierres la pestaña hasta volver a PASSAR.',
                                    'Usá la cuenta de Mercado Pago que recibirá el dinero.',
                                    'Si algo queda pendiente, podés reintentar desde esta misma pantalla.'
                                ].map((item) => (
                                    <div key={item} className="flex items-start gap-3 text-sm text-white/50 font-medium">
                                        <CheckCircle2 size={16} className="text-green-500 mt-0.5 shrink-0" />
                                        <span>{item}</span>
                                    </div>
                                ))}
                            </div>

                            <button
                                type="button"
                                onClick={startMPConnect}
                                className="w-full px-6 py-4 bg-brand-gradient text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-xl shadow-orange-500/20 hover:opacity-90 transition-all active:scale-95"
                            >
                                Entiendo, conectar Mercado Pago
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Header STICKY */}
            <div className="sticky top-16 z-40 -mx-4 px-4 py-6 mb-8 bg-background/80 backdrop-blur-md border-b border-white/5 flex flex-col md:flex-row md:items-center justify-between gap-6 transition-all">
                <div>
                    <div className="flex items-center gap-3 mb-1">
                        <div className="p-1.5 bg-primary-orange/10 rounded-lg text-primary-orange">
                            <SettingsIcon size={14} />
                        </div>
                        <span className="text-[9px] font-black uppercase tracking-[3px] text-white/30">Admin Panel</span>
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight leading-none">
                        Ajustes de <span className="text-gradient">{organizationLabel}</span>
                    </h1>
                </div>

                <button
                    onClick={handleUpdate}
                    disabled={updating}
                    className={`px-8 py-3.5 rounded-2xl font-black uppercase tracking-widest text-xs shadow-xl transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-3 group
                        ${saveStatus === 'saved' ? 'bg-green-500/10 text-green-500 border border-green-500/20 shadow-green-500/5' : 
                          saveStatus === 'error' ? 'bg-red-500/10 text-red-500 border border-red-500/20' :
                          'bg-brand-gradient text-white shadow-orange-500/20'}`}
                >
                    {saveStatus === 'saving' ? (
                        <>
                            <span>Guardando...</span>
                            <Loader2 size={16} className="animate-spin" />
                        </>
                    ) : saveStatus === 'saved' ? (
                        <>
                            <span>Cambios Guardados</span>
                            <CheckCircle2 size={16} className="animate-bounce" />
                        </>
                    ) : saveStatus === 'error' ? (
                        <>
                            <span>Error al Guardar</span>
                            <AlertCircle size={16} />
                        </>
                    ) : (
                        <>
                            <span>Guardar Ajustes</span>
                            <Save size={16} className="group-hover:rotate-12 transition-transform" />
                        </>
                    )}
                </button>
            </div>

            {onboardingHint && (
                <div className="mb-8 p-5 md:p-6 bg-primary-orange/10 border border-primary-orange/20 rounded-[28px] flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                        <div className="w-11 h-11 rounded-2xl bg-primary-orange/15 text-primary-orange flex items-center justify-center shrink-0">
                            <CheckCircle2 size={22} />
                        </div>
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-[3px] text-primary-orange/70 mb-1">Paso del onboarding</p>
                            <h2 className="text-lg font-black text-white">{onboardingHint.title}</h2>
                            <p className="text-sm text-white/50 font-medium leading-relaxed mt-1">{onboardingHint.body}</p>
                        </div>
                    </div>
                    {onboardingHint.key === 'mp' ? (
                        <button
                            type="button"
                            onClick={() => setShowMpGuide(true)}
                            className="px-5 py-3 rounded-2xl bg-brand-gradient text-white font-black uppercase tracking-widest text-[10px] shadow-lg shadow-orange-500/20"
                        >
                            Conectar ahora
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setOnboardingHint(null)}
                            className="px-5 py-3 rounded-2xl bg-white/5 text-white/50 hover:text-white border border-white/10 font-black uppercase tracking-widest text-[10px]"
                        >
                            Entendido
                        </button>
                    )}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-8">

                    {/* Identidad Visual */}
                    <section ref={brandSectionRef} className="bg-surface p-8 md:p-10 rounded-[40px] border border-white/5 shadow-2xl relative overflow-hidden group">
                        <div className="absolute top-0 left-0 w-full h-1 bg-brand-gradient opacity-30 group-hover:opacity-100 transition-opacity"></div>

                        <div className="flex items-center gap-4 mb-10">
                            <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center text-primary-orange">
                                <Store size={24} />
                            </div>
                            <div>
                                <h2 className="text-2xl font-black text-white tracking-tight">Marca</h2>
                                <p className="text-[10px] text-white/30 font-bold uppercase tracking-widest">Identidad y Descripción</p>
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row items-start gap-10">
                            <div className="shrink-0 mx-auto md:mx-0">
                                <UploadAvatar client={cliente} onUpdate={fetchCliente} />
                            </div>
                            <div className="flex-1 w-full space-y-8">
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-white/30 uppercase tracking-[2px] ml-1">Nombre Público de la Organización</label>
                                    <input
                                        type="text"
                                        value={formData.nombre}
                                        onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                        placeholder={`Ej: ${organizationLabel} Central`}
                                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-bold outline-none focus:border-primary-orange focus:bg-white/[0.07] transition-all"
                                    />
                                </div>
                                <div className="space-y-3">
                                    <label className="text-[10px] font-black text-white/30 uppercase tracking-[2px] ml-1">Biografía / Acerca de</label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        placeholder="Contanos qué hace especial a tu organización..."
                                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-medium outline-none focus:border-primary-orange focus:bg-white/[0.07] transition-all h-36 resize-none"
                                    />
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Soporte */}
                    <section ref={contactSectionRef} className="bg-surface p-8 md:p-10 rounded-[40px] border border-white/5 shadow-2xl relative overflow-hidden group">
                        <div className="flex items-center gap-4 mb-10">
                            <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center text-green-500">
                                <MessageCircle size={24} />
                            </div>
                            <div>
                                <h2 className="text-2xl font-black text-white tracking-tight">Contacto de Soporte</h2>
                                <p className="text-[10px] text-white/30 font-bold uppercase tracking-widest">Atención Post-Venta</p>
                            </div>
                        </div>

                        <p className="text-white/40 text-sm mb-8 font-medium">
                            Estos datos se utilizarán para que tus clientes puedan contactarte directamente por dudas sobre sus {cliente?.organizationType === 'sports' ? 'inscripciones' : 'entradas'}.
                        </p>

                        <div className="grid sm:grid-cols-2 gap-8">
                            <div className="space-y-3">
                                <label className="text-[10px] font-black text-white/30 uppercase tracking-[2px] ml-1 flex items-center gap-2">
                                    <Mail size={12} className="text-primary-orange" />
                                    Email de Soporte
                                </label>
                                <input
                                    type="email"
                                    value={formData.adminEmail}
                                    onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
                                    placeholder="soporte@misalon.com"
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-bold outline-none focus:border-primary-orange focus:bg-white/[0.07] transition-all"
                                />
                            </div>
                            <div className="space-y-3">
                                <label className="text-[10px] font-black text-white/30 uppercase tracking-[2px] ml-1 flex items-center gap-2">
                                    <MessageCircle size={12} className="text-green-500" />
                                    WhatsApp de Contacto
                                </label>
                                <input
                                    type="text"
                                    value={formData.contactPhone}
                                    onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                                    placeholder="Ej: 1122334455"
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-bold outline-none focus:border-primary-orange focus:bg-white/[0.07] transition-all"
                                />
                            </div>
                        </div>
                    </section>

                    {/* Ubicación */}
                    <section className="bg-surface p-8 md:p-10 rounded-[40px] border border-white/5 shadow-2xl relative overflow-hidden group">
                        <div className="flex items-center gap-4 mb-10">
                            <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center text-blue-500">
                                <MapPin size={24} />
                            </div>
                            <div>
                                <h2 className="text-2xl font-black text-white tracking-tight">Ubicación</h2>
                                <p className="text-[10px] text-white/30 font-bold uppercase tracking-widest">Direccion, Provincia y Ciudad</p>
                            </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-8">
                            <div className="space-y-3 sm:col-span-2">
                                <label className="text-[10px] font-black text-white/30 uppercase tracking-[2px] ml-1">Direccion</label>
                                <input
                                    type="text"
                                    value={formData.address}
                                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                    placeholder="Ej: Av. Siempre Viva 742"
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-bold outline-none focus:border-primary-orange focus:bg-white/[0.07] transition-all"
                                />
                            </div>
                            <div className="space-y-3">
                                <label className="text-[10px] font-black text-white/30 uppercase tracking-[2px] ml-1">Provincia</label>
                                <input
                                    type="text"
                                    value={formData.province}
                                    onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                                    placeholder="Ej: Córdoba"
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-bold outline-none focus:border-primary-orange focus:bg-white/[0.07] transition-all"
                                />
                            </div>
                            <div className="space-y-3">
                                <label className="text-[10px] font-black text-white/30 uppercase tracking-[2px] ml-1">Ciudad</label>
                                <input
                                    type="text"
                                    value={formData.city}
                                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                                    placeholder="Ej: Villa María"
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-bold outline-none focus:border-primary-orange focus:bg-white/[0.07] transition-all"
                                />
                            </div>
                        </div>
                    </section>

                    {/* Fiscal */}
                    <section className="bg-surface p-8 md:p-10 rounded-[40px] border border-white/5 shadow-2xl">
                        <div className="space-y-3">
                            <label className="text-[10px] font-black text-white/30 uppercase tracking-[2px] ml-1 flex items-center gap-2">
                                <ShieldCheck size={12} className="text-blue-400" />
                                Identificación Fiscal (CUIT)
                            </label>
                            <input
                                type="text"
                                value={formData.taxId}
                                onChange={(e) => setFormData({ ...formData, taxId: e.target.value })}
                                placeholder="20-XXXXXXXX-X"
                                className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-bold outline-none focus:border-primary-orange focus:bg-white/[0.07] transition-all"
                            />
                        </div>
                    </section>

                    {/* Comisiones */}
                    <section className="bg-surface p-8 md:p-10 rounded-[40px] border border-white/5 shadow-2xl relative overflow-hidden group">
                        <div className="flex items-center gap-4 mb-10">
                            <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center text-primary-orange">
                                <CreditCard size={24} />
                            </div>
                            <div>
                                <h2 className="text-2xl font-black text-white tracking-tight">Configuración de Comisiones</h2>
                                <p className="text-[10px] text-white/30 font-bold uppercase tracking-widest">Cargos por Servicio</p>
                            </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-8">
                            <div className="space-y-3">
                                <label className="text-[10px] font-black text-white/30 uppercase tracking-[2px] ml-1">¿Quién paga la comisión?</label>
                                <select
                                    value={cliente?.commissionConfig?.payer || 'customer'}
                                    onChange={(e) => setCliente({
                                        ...cliente,
                                        commissionConfig: { ...cliente.commissionConfig, payer: e.target.value }
                                    })}
                                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white font-bold outline-none focus:border-primary-orange focus:bg-white/[0.07] transition-all appearance-none"
                                >
                                    <option value="salon" className="bg-surface">{organizationLabel} (se descuenta del total)</option>
                                    <option value="customer" className="bg-surface">El cliente (se suma al precio)</option>
                                </select>
                            </div>
                            <div className="space-y-3">
                                <label className="text-[10px] font-black text-white/30 uppercase tracking-[2px] ml-1">Porcentaje de Cargo (Fijo)</label>
                                <div className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-white/40 font-bold">
                                    {serviceChargePercentage}%
                                </div>
                            </div>
                        </div>

                        <div className="mt-8 p-6 bg-white/[0.02] border border-white/5 rounded-3xl">
                            <h4 className="text-[10px] font-black text-white/40 uppercase tracking-widest mb-4 flex items-center gap-2">
                                <Info size={12} className="text-primary-orange" />
                                Ejemplo de cálculo ({organizationPaysCommission ? `${organizationLabel} absorbe el cargo` : 'El cliente paga'})
                            </h4>
                            <div className="space-y-3 text-xs">
                                <div className="flex justify-between text-white/60">
                                    <span>Valor de tu {cliente?.organizationType === 'sports' ? 'inscripción' : 'entrada'}:</span>
                                    <span className="font-bold text-white">$1.000</span>
                                </div>
                                {!organizationPaysCommission && (
                                    <div className="flex justify-between text-white/60">
                                        <span>Cargo por servicio ({serviceChargePercentage}%):</span>
                                        <span className="font-bold text-white">${serviceChargeExample}</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-primary-orange pt-2 border-t border-white/5 font-black">
                                    <span>El cliente paga:</span>
                                    <span>${customerTotalExample.toLocaleString('es-AR')}</span>
                                </div>
                                {organizationPaysCommission && (
                                    <div className="flex justify-between text-white/60">
                                        <span>Comisión PASSAR ({platformFeePercentage}%):</span>
                                        <span className="font-bold text-white">-${platformFeeExample.toLocaleString('es-AR')}</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-green-400 pt-2 border-t border-white/5 font-black">
                                    <span>{organizationLabel} recibe:</span>
                                    <span>${organizationReceivesExample.toLocaleString('es-AR')}</span>
                                </div>
                                <div className="mt-4 space-y-2 text-[10px] text-white/30 leading-relaxed">
                                    {organizationPaysCommission ? (
                                        <>
                                            <p>• El cliente ve el valor publicado de la entrada, sin recargo.</p>
                                            <p>• PASSAR retiene el {platformFeePercentage}% del importe cobrado.</p>
                                        </>
                                    ) : isSportsClient ? (
                                        <>
                                            <p>• PASSAR retiene un 5% por el servicio al procesar el pago.</p>
                                            <p>• Podés elegir si el cliente absorbe el cargo en el checkout.</p>
                                        </>
                                    ) : (
                                        <>
                                            <p>• PASSAR cobra un 4% por el servicio.</p>
                                            <p>• El 10% restante queda acreditado a tu operación para cubrir costos de cobro.</p>
                                            <p className="text-green-500/50 font-bold">• Resultado: recibís el valor publicado de tu entrada ($1.000).</p>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        <p className="mt-6 text-xs text-white/40 leading-relaxed italic">
                            {organizationPaysCommission
                                ? `El cliente verá el precio publicado. PASSAR descontará su comisión del pago acreditado a ${organizationLabel.toLowerCase()}.`
                                : `El cliente verá el precio de la ${cliente?.organizationType === 'sports' ? 'inscripción' : 'entrada'} más el cargo por servicio en el checkout.`}
                        </p>
                        <p className="mt-4 text-[10px] text-white/20 font-medium leading-relaxed border-t border-white/5 pt-4">
                            {isSportsClient
                                ? '* La configuración de complejos deportivos conserva su esquema actual de venta e inscripción.'
                                : '* El 14% se compone de 4% para PASSAR y 10% acreditado a tu operación. Así el cargo no reduce el valor publicado de la entrada.'}
                        </p>
                    </section>

                    <div className="pt-8 flex justify-center md:justify-end">
                        <button
                            onClick={handleUpdate}
                            disabled={updating}
                            className={`w-full md:w-auto px-12 py-5 rounded-3xl font-black uppercase tracking-widest text-sm shadow-2xl transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-4 group
                                ${saveStatus === 'saved' ? 'bg-green-500/10 text-green-500 border border-green-500/20 shadow-green-500/5' : 
                                  saveStatus === 'error' ? 'bg-red-500/10 text-red-500 border border-red-500/20' :
                                  'bg-brand-gradient text-white shadow-orange-500/20'}`}
                        >
                            {saveStatus === 'saving' ? (
                                <>
                                    <Loader2 size={20} className="animate-spin" />
                                    <span>Guardando Ajustes...</span>
                                </>
                            ) : saveStatus === 'saved' ? (
                                <>
                                    <CheckCircle2 size={20} className="animate-bounce" />
                                    <span>Cambios Guardados</span>
                                </>
                            ) : saveStatus === 'error' ? (
                                <>
                                    <AlertCircle size={20} />
                                    <span>Error al Guardar</span>
                                </>
                            ) : (
                                <>
                                    <Save size={20} className="group-hover:rotate-12 transition-transform" />
                                    <span>Confirmar Cambios</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Pagos */}
                <div className="space-y-8">
                    <section ref={mpSectionRef} data-tour="settings-mp" className="bg-surface p-10 rounded-[40px] border border-white/5 shadow-2xl relative overflow-hidden text-center group">
                        <div className={`absolute top-0 left-0 w-full h-1 ${cliente?.hasMpConnected ? 'bg-green-500' : 'bg-red-500/30'}`}></div>

                        <div className={`w-24 h-24 rounded-3xl mx-auto flex items-center justify-center mb-8 shadow-2xl transition-all duration-700 ${cliente?.hasMpConnected ? 'bg-green-500/10 text-green-500' : 'bg-white/5 text-white/20'}`}>
                            <CreditCard size={48} className={cliente?.hasMpConnected ? 'animate-pulse' : ''} />
                        </div>

                        <h3 className="text-2xl font-black text-white mb-2 leading-none">
                            {cliente?.hasMpConnected ? 'Conectado' : 'Desconectado'}
                        </h3>
                        <p className="text-[10px] font-black uppercase tracking-[3px] text-white/20 mb-8">Mercado Pago</p>

                        <p className="text-sm text-white/40 mb-10 leading-relaxed font-medium">
                            {cliente?.hasMpConnected
                                ? 'Los pagos se acreditan automáticamente en tu cuenta verificada.'
                                : `Conectá tu cuenta para empezar a cobrar ${cliente?.organizationType === 'sports' ? 'inscripciones' : 'entradas'} online.`}
                        </p>

                        {cliente?.hasMpConnected ? (
                            <button
                                onClick={handleMPDisconnect}
                                className="w-full px-6 py-4 bg-red-500/10 text-red-500 border border-red-500/20 rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-red-500 hover:text-white transition-all active:scale-95"
                            >
                                Desvincular Cuenta
                            </button>
                        ) : (
                            <button
                                onClick={handleMPConnect}
                                className="w-full px-6 py-4 bg-brand-gradient text-white rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-xl shadow-orange-500/20 hover:opacity-90 transition-all active:scale-95"
                            >
                                Vincular Mercado Pago
                            </button>
                        )}
                    </section>

                    <div className="p-8 bg-glass rounded-[40px] border border-white/5 shadow-2xl">
                        <div className="flex items-center gap-3 mb-4">
                            <AlertCircle size={16} className="text-primary-orange" />
                            <h4 className="text-[10px] font-black text-white uppercase tracking-widest">Consejo</h4>
                        </div>
                        <p className="text-xs text-white/40 leading-relaxed font-medium italic">
                            "Un buen logo y una biografía clara generan confianza inmediata en tus clientes antes de comprar."
                        </p>
                    </div>

                    {/* Danger Zone */}
                    <section className="mt-12 bg-surface p-10 rounded-[40px] border border-red-500/20 shadow-[0_0_50px_rgba(239,68,68,0.05)] relative overflow-hidden group">
                        <div className="absolute top-0 left-0 w-full h-1 bg-red-500/50"></div>
                        
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-12 h-12 bg-red-500/10 rounded-2xl flex items-center justify-center text-red-500">
                                <AlertCircle size={24} />
                            </div>
                            <div>
                                <h2 className="text-2xl font-black text-white tracking-tight">Zona de Peligro</h2>
                                <p className="text-[10px] text-red-400 font-bold uppercase tracking-widest">Eliminación Definitiva</p>
                            </div>
                        </div>

                        <p className="text-sm text-white/40 mb-8 leading-relaxed font-medium">
                            Al eliminar esta organización, cancelarás automáticamente la suscripción o débitos automáticos que puedan estar activos en tu Mercado Pago asociados a PASSAR. Esta acción también borrará tu base de datos de eventos, tickets, roles de equipo y tu enlace personalizado de manera permanente e irreversible.
                        </p>

                        <button
                            onClick={handleDeleteOrganization}
                            disabled={updating}
                            className="w-full sm:w-auto px-8 py-4 bg-red-500/10 text-red-500 border border-red-500/20 rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-red-500 hover:text-white transition-all active:scale-95 disabled:opacity-50"
                        >
                            Eliminar Organización Definitivamente
                        </button>
                    </section>
                </div>
            </div>

            {/* Botón flotante para subir rápido (UX) */}
            <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="fixed bottom-8 right-8 w-12 h-12 bg-surface border border-white/10 rounded-full flex items-center justify-center text-white/40 hover:text-primary-orange hover:border-primary-orange/50 transition-all shadow-2xl z-50 backdrop-blur-md"
            >
                <ArrowUp size={20} />
            </button>
        </div>
    );
};

export default Settings;
